import React, { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext(null);

const STORAGE_KEY = "sjm_lang";

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "en"; } catch { return "en"; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    document.documentElement.lang = lang === "bn" ? "bn" : "en";
  }, [lang]);

  const toggle = () => setLang((l) => (l === "en" ? "bn" : "en"));

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

// Translation dictionary
export const T = {
  org_name: { en: "Sangrami Joutha Mancha", bn: "সংগ্রামী যৌথ মঞ্চ" },
  org_tag: { en: "United Front of Struggle", bn: "সংগ্রামী যৌথ মঞ্চ" },
  nav: {
    home: { en: "Home", bn: "মূল পাতা" },
    about: { en: "About", bn: "আমাদের সম্পর্কে" },
    campaigns: { en: "Campaigns", bn: "আন্দোলন" },
    news: { en: "News", bn: "খবর" },
    events: { en: "Events", bn: "অনুষ্ঠানসূচি" },
    leadership: { en: "Leadership", bn: "নেতৃত্ব" },
    gallery: { en: "Gallery", bn: "চিত্রশালা" },
    join: { en: "Join", bn: "যোগ দিন" },
    contact: { en: "Contact", bn: "যোগাযোগ" },
  },
  hero: {
    badge: { en: "Justice for every worker — organised & unorganised", bn: "প্রতিটি কর্মীর জন্য ন্যায়বিচার — সংগঠিত ও অসংগঠিত" },
    title_en: "We fight together. We win together.",
    title_bn: "একসাথে লড়ি। একসাথে জিতি।",
    subtitle: {
      en: "A united front of West Bengal's organised and unorganised employees demanding dignity, fair wages, and justice — at the streets, courts and corridors of power.",
      bn: "পশ্চিমবঙ্গের সংগঠিত ও অসংগঠিত কর্মীদের যুক্ত সংগ্রামী মঞ্চ — মর্যাদা, ন্যায্য মজুরি ও ন্যায়বিচারের দাবিতে রাজপথ থেকে আদালত পর্যন্ত আমরা লড়ছি।",
    },
    cta_join: { en: "Join the Movement", bn: "আন্দোলনে যোগ দিন" },
    cta_donate: { en: "Support the Cause", bn: "সমর্থন করুন" },
  // Contact for complaints
  complaint_email: { en: "sjmcomplaint@gmail.com", bn: "sjmcomplaint@gmail.com" },
  complaint_note: {
    en: "Please submit your complaint about revenge, corruption, or threats, with proof if possible, to the email above. If it is true, we shall fight for the cause.",
    bn: "অনুগ্রহ করে প্রতিশোধ, দুর্নীতি বা হুমকির বিষয়ে আপনার অভিযোগ থাকলে, সম্ভব হলে প্রমাণসহ উপরের ইমেইলে পাঠান। যদি অভিযোগ সত্য প্রমাণিত হয়, আমরা সেই কারণের জন্য লড়াই করব।",
  },
  live_visitors: { en: "Live visitors online", bn: "লাইভ দর্শক অনলাইন" },
  },
  ticker: {
    items: {
      en: ["Restore Pending DA", "Justice for Contract Workers", "Minimum Wage for Unorganised Workers", "Regularise Long-serving Employees", "Fair Pension for All", "Right to Dignified Work"],
      bn: ["বকেয়া ডিএ পুনর্বহাল", "চুক্তিভিত্তিক কর্মীদের ন্যায়বিচার", "অসংগঠিত কর্মীদের ন্যূনতম মজুরি", "দীর্ঘ সেবার কর্মীদের নিয়মিতকরণ", "সকলের জন্য ন্যায্য পেনশন", "মর্যাদাপূর্ণ কাজের অধিকার"],
    },
  },
  about: {
    eyebrow: { en: "WHO WE ARE", bn: "আমরা কারা" },
    title: { en: "A movement of workers, by workers, for workers.", bn: "কর্মীদের আন্দোলন, কর্মীদের জন্য, কর্মীদের দ্বারা।" },
    p1: {
      en: "Sangrami Joutha Mancha is a coalition of trade unions, employee associations and citizens fighting for the constitutional and human rights of every employee in West Bengal — government, contract, daily-wage, and unorganised sector workers alike.",
      bn: "সংগ্রামী যৌথ মঞ্চ হল ট্রেড ইউনিয়ন, কর্মচারী সংগঠন ও নাগরিকদের এক যুক্ত মঞ্চ। আমরা পশ্চিমবঙ্গের প্রতিটি কর্মীর — সরকারি, চুক্তিভিত্তিক, দৈনিক মজুরি ও অসংগঠিত ক্ষেত্রের কর্মীর — সাংবিধানিক ও মানবিক অধিকারের জন্য লড়ছি।",
    },
    p2: {
      en: "We organise rallies, file public-interest petitions, document violations, and amplify the voice of every working person who has been denied dignity, dues or due process.",
      bn: "আমরা মিছিল সংগঠিত করি, জনস্বার্থ মামলা দাখিল করি, লঙ্ঘনের নথি সংগ্রহ করি, এবং প্রতিটি কর্মীর কণ্ঠস্বর তুলে ধরি যাঁরা তাঁদের মর্যাদা, প্রাপ্য বা ন্যায়বিচার থেকে বঞ্চিত।",
    },
    pillars: {
      en: [
        { k: "01", t: "Dignity", d: "Every worker, organised or not, deserves a dignified livelihood." },
        { k: "02", t: "Solidarity", d: "Across sectors, districts and unions — we stand united." },
        { k: "03", t: "Constitutional Justice", d: "We pursue legal and constitutional channels alongside grassroots action." },
      ],
      bn: [
        { k: "০১", t: "মর্যাদা", d: "সংগঠিত বা অসংগঠিত — প্রতিটি কর্মীর মর্যাদাপূর্ণ জীবিকা প্রাপ্য।" },
        { k: "০২", t: "সংহতি", d: "বিভিন্ন ক্ষেত্র, জেলা ও ইউনিয়ন জুড়ে আমরা একতাবদ্ধ।" },
        { k: "০৩", t: "সাংবিধানিক ন্যায়বিচার", d: "তৃণমূল আন্দোলনের পাশাপাশি আইনি ও সাংবিধানিক পথে এগোই।" },
      ],
    },
  },
  campaigns: {
    eyebrow: { en: "ONGOING CAMPAIGNS", bn: "চলমান আন্দোলন" },
    title: { en: "What we are fighting for, right now.", bn: "এখন আমরা কীসের জন্য লড়ছি।" },
    items: {
      en: [
        "End the culture of threats in offices permanently",
        "Identify and punish those who enable corruption and patronise the culture of threats",
        "Immediately reinstate all employees who were punished or removed by the government for protesting",
        "Ensure transparency in transfers and promotions",
      ],
      bn: [
        "দপ্তরে দপ্তরে থ্রেট কালচার চিরদিনের মতন বন্ধ করতে হবে",
        "দুর্নীতির মদত দাতা ও থ্রেট কালচারের পৃষ্ঠপোষকদের চিহ্নিত করে শাস্তির ব্যবস্থা করতে হবে।",
        "সমস্ত কর্মচারী প্রতিবাদ করার জন্য সরকার থেকে শাস্তিমূলকভাবে দূরে পাঠিয়ে দেওয়া হয়েছিল তাদের অবিলম্বে ফিরিয়ে আনতে হবে।",
        "বদলি ও পদোন্নতির স্বচ্ছতা প্রণয়ন করতে হবে।",
      ],
    },
    statement: {
      en: "Our struggle against injustice and corruption will continue unabated. We will never tolerate becoming party stooges to allow injustice. We have leadership and the right direction, and our unity will help us in bigger struggles ahead. We are not against the government — we are ready to help it — but we will protest when it acts unjustly.",
      bn: "কোন অবস্থাতেই অন্যায় ও দুর্নীতির বিরুদ্ধে আমাদের লড়াই আগের মতই জারি থাকবে। দলদাস হয়ে অন্যায় অবিচার আমরা কোনদিনই সহ্য করব না। এইসব নেতৃত্ব আর সঠিক দিশা আমাদের আছে; আমরা নিশ্চিত আমাদের ঐক্য আগামী দিনে আমাদের অনেক বড় লড়াইয়ে সাহায্য করবে। আমরা সরকারের বিরোধী নই; সরকারকে সাহায্য করতে প্রস্তুত, কিন্তু অন্যায় করলে তার প্রতিবাদ করতেও আমরা প্রস্তুত।",
    },
    status_active: { en: "Active", bn: "চলমান" },
    status_past: { en: "Past", bn: "পূর্ববর্তী" },
  },
  news: {
    eyebrow: { en: "FROM THE FIELD", bn: "মাঠ থেকে" },
    title: { en: "Latest news & updates", bn: "সর্বশেষ খবর ও আপডেট" },
    read: { en: "Read", bn: "পড়ুন" },
  },
  announcements: {
    rakhibandhan: {
      title: { en: "Rakhibandhan announcement", bn: "রাখিবন্ধনের শুভক্ষণে" },
      body: {
        en: "🌿 On the auspicious occasion of Rakhi Bandhan\nA new pledge 🌿\n\nThis coming Friday, 28 August 2026, on the sacred day of Rakhi Bandhan, at 4:00 PM at Kolkata Press Club, a new journey will begin.\nInspired by the spirit of Sangrami Joutha Mancha, the organisation will emerge to further strengthen the unity of government employees.\n✊ State Sangrami Karmachari Sangathan ✊\nThis is not only the inauguration of a new organisation—it is a resolute step toward strengthening the unity, rights, dignity and collective struggle of employees across the state.\nAlthough this movement begins with the Fisheries Department unit in its first phase, our dream and goal are much larger. Let unity, mutual cooperation, organisational awareness, and determination to protect rights take root in every department, workplace and corner of the state.\nWe sincerely invite all employee friends, companions, well-wishers and retired colleagues—\nCome join us at the Press Club on 28 August. Be a witness to this new movement.",
        bn: "🌿 রাখিবন্ধনের শুভক্ষণে\nএক নতুন অঙ্গীকার 🌿\n\nআগামী শুক্রবার, ২৮ আগস্ট ২০২৬, রাখিবন্ধনের পবিত্র দিনে বিকেল ৪টায় কলকাতা প্রেস ক্লাবে এক নতুন পথচলার শুভ সূচনা হতে চলেছে--\nসংগ্রামী যৌথ মঞ্চের ভাবধারায় পুষ্ট হয়ে সরকারি কর্মচারীদের ঐক্যকে আরও শক্তিশালী করার লক্ষ্যে আত্মপ্রকাশ করবে -\n✊ রাজ্য সংগ্রামী কর্মচারী সংগঠন ✊\nএটি শুধু একটি নতুন সংগঠনের উদ্বোধন নয়—এটি রাজ্যের আপামর কর্মচারীর ঐক্য, অধিকার, মর্যাদা ও সম্মিলিত সংগ্রামকে আরও সুদৃঢ় ও সুসংহত করার একটি প্রত্যয়ী পদক্ষেপ।\nপ্রথম পর্বে মৎস্য দপ্তর ইউনিটের মাধ্যমে এই পথচলা শুরু হলেও আমাদের স্বপ্ন ও লক্ষ্য অনেক বৃহৎ। রাজ্যের প্রতিটি দপ্তর, প্রতিটি কর্মস্থল এবং প্রতিটি প্রান্তে কর্মচারীদের মধ্যে গড়ে উঠুক ঐক্য, পারস্পরিক সহযোগিতা, সাংগঠনিক সচেতনতা ও অধিকার রক্ষার দৃঢ় প্রত্যয়।\nআমরা আন্তরিকভাবে আহ্বান জানাচ্ছি রাজ্যের সকল কর্মচারী বন্ধু, সহযোদ্ধা, শুভানুধ্যায়ী এবং অবসরপ্রাপ্ত সহকর্মীদের—\nআসুন, ২৮ আগস্ট প্রেস ক্লাবে আমাদের সঙ্গে থাকুন। এই নতুন পথচলার সাক্ষী হোন।",
      },
      timestamp: "2026-08-28",
    },
    foodCampus: {
      title: { en: "Food Bhavan campus discussion", bn: "খাদ্য ভবন ক্যাম্পাসে আলোচনা" },
      body: {
        en: "Today, the keynote convenor of Sangrami Joutha Mancha, Shri Bhaskar Ghosh, is present at the Food Bhavan campus.\nThe main discussion is about a matter concerning the State Small Irrigation Corporation, along with several other important related issues.",
        bn: "খাদ্য ভবন ক্যাম্পাসে আজ উপস্থিত হয়েছেন সংগ্রামী যৌথ মঞ্চের মুখ্য কনভেনার শ্রী ভাস্কর ঘোষ মহাশয়।\nরাজ্য ক্ষুদ্র সেচ নিগমের একটি বিষয়ে মূলত আলোচনা চলছে। তৎসহ আনুষঙ্গিক কিছু গুরুত্বপূর্ণ আলোচনাও চলছে।",
      },
      timestamp: "2026-08-20",
    },
    facebook: {
      label: { en: "View on Facebook", bn: "ফেসবুকে দেখুন" },
    },
  },
  events: {
    eyebrow: { en: "WHAT'S NEXT", bn: "আগামী" },
    title: { en: "Upcoming rallies, conventions & meetings", bn: "আগামী মিছিল, কনভেনশন ও সভা" },
    none: { en: "No upcoming events scheduled.", bn: "আপাতত কোনো অনুষ্ঠান নির্ধারিত নেই।" },
    on_date: { en: "On this date", bn: "এই তারিখে" },
  },
  leaders: {
    eyebrow: { en: "LEADERSHIP", bn: "নেতৃত্ব" },
    title: { en: "Voices behind the movement", bn: "আন্দোলনের পেছনের কণ্ঠস্বর" },
  },
  gallery: {
    eyebrow: { en: "GALLERY", bn: "চিত্রশালা" },
    title: { en: "Faces. Streets. Solidarity.", bn: "মুখ। রাজপথ। সংহতি।" },
  },
  join: {
    eyebrow: { en: "JOIN US", bn: "আমাদের সঙ্গে যোগ দিন" },
    title: { en: "Add your name to the struggle.", bn: "সংগ্রামে আপনার নাম যুক্ত করুন।" },
    subtitle: { en: "Tell us who you are. Together we are louder.", bn: "আপনি কে আমাদের জানান। একসাথে আমাদের কণ্ঠ আরও জোরালো।" },
    name: { en: "Full Name", bn: "পুরো নাম" },
    phone: { en: "Phone Number", bn: "ফোন নম্বর" },
    profession: { en: "Profession", bn: "পেশা" },
    office: { en: "Office", bn: "অফিস" },
    district: { en: "District", bn: "জেলা" },
    message: { en: "Message (optional)", bn: "বার্তা (ঐচ্ছিক)" },
    submit: { en: "I'm In", bn: "আমি যুক্ত হলাম" },
    success: { en: "Thank you. We'll be in touch soon.", bn: "ধন্যবাদ। আমরা শীঘ্রই যোগাযোগ করব।" },
    fail: { en: "Could not submit. Please try again.", bn: "জমা দেওয়া যায়নি। আবার চেষ্টা করুন।" },
  },
  contact: {
    eyebrow: { en: "CONTACT", bn: "যোগাযোগ" },
    title: { en: "Reach out. We listen.", bn: "যোগাযোগ করুন। আমরা শুনছি।" },
    address: { en: "Kolkata, West Bengal, India", bn: "কলকাতা, পশ্চিমবঙ্গ, ভারত" },
  },
  footer: {
    line1: { en: "Solidarity is our weapon.", bn: "সংহতিই আমাদের অস্ত্র।" },
    rights: { en: "All rights reserved.", bn: "সর্বস্বত্ব সংরক্ষিত।" },
  },
  admin: {
    title: { en: "Admin Dashboard", bn: "অ্যাডমিন ড্যাশবোর্ড" },
    login: { en: "Admin Login", bn: "অ্যাডমিন লগইন" },
    logout: { en: "Logout", bn: "লগআউট" },
  },
};

export function pickBilingual(item, field, lang) {
  if (!item) return "";
  const k = `${field}_${lang}`;
  if (item[k]) return item[k];
  return item[`${field}_en`] || item[`${field}_bn`] || "";
}
